/* eslint-disable react-hooks/set-state-in-effect */
// frontend/src/components/charts/BarEnvelopeChart.jsx
// Stacked bars per group over time, with per-group forecast lines, a
// projected envelope and the markers of the store's calendar.
//
// Props:
//   useData             — data hook (sales, demand timeline, inventory timeline)
//   i18nPrefix          — translation key prefix
//   chartId             — key the user's metric, granularity and zoom are saved under
//   controls            — { metric, granularity } booleans
//   defaultGranularity  — 'month' | 'week' | 'day', what the chart opens on before the user picks
//   months              — months to fetch
//   historyMonths       — past months to fetch
//   extraParams         — page filter scope, passed to the hook as `scope`
//   navigator           — show the navigator and zoom
//   navigatorWindow     — { before, after } periods around today the navigator opens on
//   noDataKey           — i18n key for an empty timeline, in place of `${i18nPrefix}.noData`
//   importingReason     — empty reason shown in place of the plot while the store imports
//   onHistoryPresence   — called with whether the payload holds any bucket before today's

import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PartialPriceChip from '../reports/PartialPriceChip';
import { Button, CircularProgress, Typography } from '@mui/material';
import TabLink from '../../tabs/TabLink';
import Chart from './Chart';
import { useFormatters } from '../../hooks/useFormatters';
import { useChartTheme } from '../../hooks/useChartTheme';
import { useChartPreferences } from '../../hooks/useChartPreferences';
import { EMPTY_REASON_KEYS, normalizeTimelinePayload } from './barEnvelopeData';
import { sumOfParts, tooltipTotalHtml } from './tooltipTotal';
import { bucketOpeningOf, periodBucket, periodToDate } from '../../utils/chartPeriods';
import { headroomMax } from './axisHeadroom';
import './BarEnvelopeChart.css';

// A group gets its own stack when it carries at least this share of the
// chart's total, as long as the palette has a color left for "Other"; the
// rest is one "Other" stack, so "Other" is always a tail of small groups.
// A lone remainder keeps its name.
const MIN_GROUP_SHARE = 0.05;
// The "Other" stack's data key: a group's key is its label, and a group may
// itself be labelled "Other" (a product type), so the bucket has its own.
const OTHER_KEY = '__other__';
const ENVELOPE_KEY = '__envelope__';
const RECOMMENDATION_PAB_KEY = '__recommendation_pab__';
const TARGET_INVENTORY_KEY = '__target_inventory__';
const BAR_TOTAL_KEY = '__bar_total__';
const FORECAST_TOTAL_KEY = '__forecast_total__';
const TOTAL_KEY = '__total__';
// Series the legend may toggle that are not a group: a click on them changes
// no group's visibility.
const NON_GROUP_KEYS = new Set([ENVELOPE_KEY, RECOMMENDATION_PAB_KEY, TARGET_INVENTORY_KEY, TOTAL_KEY]);

const DEFAULT_CONTROLS = { metric: true, granularity: false };

export function SegmentControl({ options, value, onChange }) {
  return (
    <div className="segment-control" role="group">
      {options.map(opt => (
        <button
          key={opt.value}
          type="button"
          role="radio"
          aria-pressed={value === opt.value}
          onClick={() => onChange(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function sumVisibleForPeriod(groups, period, topGroupNames, hiddenGroups) {
  let total = 0;
  let hasData = false;
  for (const g of groups) {
    const key = topGroupNames.has(g.category_label) ? g.category_label : OTHER_KEY;
    if (hiddenGroups.has(key)) continue;
    const match = g.data.find(d => d.period === period);
    if (match && match.value !== null) {
      total += parseFloat(match.value);
      hasData = true;
    }
  }
  return hasData ? total : null;
}

/**
 * The projected envelope at ``period``: the served total while every group
 * is visible, else the sum of the visible ``groups``.
 */
function calcEnvelope(period, totals, groups, topGroupNames, hiddenGroups) {
  if (hiddenGroups.size === 0) {
    const m = totals?.find(t => t.period === period);
    return m?.total != null ? parseFloat(m.total) : null;
  }
  return sumVisibleForPeriod(groups, period, topGroupNames, hiddenGroups);
}

function BarEnvelopeChart({
  useData,
  i18nPrefix,
  chartId,
  controls: controlsProp,
  defaultGranularity = 'month',
  months: monthsProp,
  historyMonths,
  extraParams = {},
  navigator: showNavigator = false,
  navigatorWindow,
  noDataKey,
  importingReason,
  onHistoryPresence,
}) {
  const { t } = useTranslation();
  const { formatNumber, formatCompact, formatCurrency, formatPeriodLabel } = useFormatters();
  const theme = useChartTheme();
  const controls = { ...DEFAULT_CONTROLS, ...controlsProp };

  const { metric, setMetric, granularity, setGranularity, navigatorZoom: savedNavigatorZoom, setNavigatorZoom } =
    useChartPreferences(chartId, {
      metric: 'value',
      granularity: defaultGranularity,
      chartType: 'bars-area',
    });

  const [hiddenGroups, setHiddenGroups] = useState(new Set());

  useEffect(() => {
    setHiddenGroups(new Set());
  }, [metric, granularity]);

  const hookParams = { metric, granularity, months: monthsProp, historyMonths, scope: extraParams };
  const { data: fetchedData, loading, error } = useData(hookParams);

  const uncategorizedLabel = t('charts.common.uncategorized');
  const { historyData, projectionData, currentPeriod, allPeriods, hasHistory, emptyReason } =
    useMemo(() => normalizeTimelinePayload(fetchedData, uncategorizedLabel), [fetchedData, uncategorizedLabel]);

  useEffect(() => {
    if (fetchedData && onHistoryPresence) onHistoryPresence(hasHistory);
  }, [fetchedData, hasHistory, onHistoryPresence]);

  const { chartData, seriesConfig, yMaxPadded, totalLabels } = useMemo(() => {
    if (!historyData?.groups || historyData.groups.length === 0 || allPeriods.length === 0) {
      return { chartData: [], seriesConfig: [], totalLabels: null };
    }

    const historyGroups = historyData.groups;
    const projectionGroups = projectionData?.groups;

    const hasProjection = projectionData != null;
    const hasForecastOverlay = Boolean(historyData?.forecast_totals);
    const hasForecastGroups = Boolean(historyData?.forecast_groups?.length);
    const hasEnvelope = hasProjection && !hasForecastOverlay;

    const forecastGroupMap = new Map();
    if (hasForecastGroups) {
      for (const fg of historyData.forecast_groups) {
        const dataMap = new Map();
        for (const d of fg.data) dataMap.set(d.period, d.value);
        forecastGroupMap.set(fg.category_label, dataMap);
      }
    }

    // A group's weight is what it sold in the shown history; its forecast
    // line follows the ranking, never sets it.
    const groupMap = new Map();
    for (const g of historyGroups) {
      const total = g.data.reduce((sum, d) => sum + parseFloat(d.value ?? 0), 0);
      groupMap.set(g.category_label, { ...g, total });
    }
    if (projectionGroups) {
      for (const pg of projectionGroups) {
        if (!groupMap.has(pg.category_label)) {
          groupMap.set(pg.category_label, { category_label: pg.category_label, data: [], total: 0 });
        }
      }
    }

    const groupTotals = [...groupMap.values()];
    groupTotals.sort((a, b) => b.total - a.total);
    const windowTotal = groupTotals.reduce((sum, g) => sum + g.total, 0);

    const { fills, strokes } = theme.palette;
    const maxNamed = fills.length - 1;
    const topGroups = [];
    const otherGroups = [];
    for (const g of groupTotals) {
      const named = topGroups.length < maxNamed
        && windowTotal > 0 && g.total / windowTotal >= MIN_GROUP_SHARE;
      (named ? topGroups : otherGroups).push(g);
    }
    if (otherGroups.length === 1) topGroups.push(otherGroups.pop());

    const otherLabel = t(`${i18nPrefix}.other`);
    const topGroupNames = new Set(topGroups.map(g => g.category_label));
    const otherGroupsExist = otherGroups.length > 0;

    // A series key is the group's label, except the "Other" stack's.
    const allGroupKeys = [...topGroups.map(g => g.category_label)];
    if (otherGroupsExist) allGroupKeys.push(OTHER_KEY);
    const labelOf = (key) => (key === OTHER_KEY ? otherLabel : key);
    const visibleGroupKeys = allGroupKeys.filter(key => !hiddenGroups.has(key));

    const chartData = allPeriods.map(period => {
      const row = {
        date: periodToDate(period),
        period,
      };

      const resolveValue = (g) => {
        if (hasForecastOverlay) {
          const match = g.data.find(d => d.period === period);
          if (!match || match.value == null) return null;
          return parseFloat(match.value);
        }
        const match = g.data.find(d => d.period === period);
        return match?.value != null ? parseFloat(match.value) : 0;
      };

      topGroups.forEach(g => { row[g.category_label] = resolveValue(g); });

      if (otherGroupsExist) {
        const otherVals = otherGroups.map(g => resolveValue(g));
        const allNull = otherVals.every(v => v === null);
        row[OTHER_KEY] = allNull ? null : otherVals.reduce((sum, v) => sum + (v ?? 0), 0);
      }

      if (hasForecastGroups) {
        topGroups.forEach(g => {
          const fcKey = `fc_${g.category_label}`;
          const val = forecastGroupMap.get(g.category_label)?.get(period);
          row[fcKey] = val != null ? parseFloat(val) : null;
        });
        if (otherGroupsExist) {
          const fcKey = `fc_${OTHER_KEY}`;
          const present = otherGroups
            .map(g => forecastGroupMap.get(g.category_label)?.get(period))
            .filter(v => v != null);
          row[fcKey] = present.length ? present.reduce((sum, v) => sum + parseFloat(v), 0) : null;
        }
      }

      row[BAR_TOTAL_KEY] = sumOfParts(visibleGroupKeys.map(key => row[key]));
      row[FORECAST_TOTAL_KEY] = hasForecastGroups
        ? sumOfParts(visibleGroupKeys.map(key => row[`fc_${key}`]))
        : null;
      row[TOTAL_KEY] = row[BAR_TOTAL_KEY] ?? row[FORECAST_TOTAL_KEY];

      if (hasEnvelope) {
        row[ENVELOPE_KEY] = period < currentPeriod ? null : calcEnvelope(
          period, historyData.totals, historyGroups, topGroupNames, hiddenGroups);
      }

      if (historyData?.recommendation_pab_totals) {
        const rpMatch = historyData.recommendation_pab_totals.find(t => t.period === period);
        row[RECOMMENDATION_PAB_KEY] = rpMatch?.total != null ? parseFloat(rpMatch.total) : null;
      }

      if (fetchedData?.target_inventory != null) {
        row[TARGET_INVENTORY_KEY] = fetchedData.target_inventory;
      }

      return row;
    });

    const seriesConfig = [];

    const salesLabel = hasForecastGroups ? t(`${i18nPrefix}.sales`) : null;
    const forecastLabel = hasForecastGroups ? t(`${i18nPrefix}.forecast`) : null;

    // "Other" is a remainder, not a group: it takes the app's neutral grays,
    // never a palette color, so it reads as the tail it is.
    allGroupKeys.forEach((key, idx) => {
      const name = labelOf(key);
      seriesConfig.push({
        type: 'bar', xKey: 'date', yKey: key,
        yName: hasForecastGroups ? `${name} — ${salesLabel}` : name,
        stacked: true,
        fill: key === OTHER_KEY ? theme.gridColor : fills[idx],
        ...(hasForecastGroups ? { legendItemName: name } : {}),
      });
    });

    if (hasForecastGroups) {
      const estimated = { key: 'x', segments: [{ start: periodToDate(currentPeriod), lineDash: [6, 4] }] };
      allGroupKeys.forEach((key, idx) => {
        const fcKey = `fc_${key}`;
        const hasFcData = chartData.some(row => row[fcKey] != null);
        if (!hasFcData) return;
        const name = labelOf(key);
        const stroke = key === OTHER_KEY ? theme.mutedColor : strokes[idx];
        seriesConfig.push({
          type: 'line', xKey: 'date', yKey: fcKey,
          yName: `${name} — ${forecastLabel}`,
          legendItemName: name,
          showInLegend: false,
          stroke,
          stacked: true,
          segmentation: estimated,
        });
      });
    }

    if (hasEnvelope && chartData.some(row => row[ENVELOPE_KEY] != null)) {
      seriesConfig.push({
        type: 'area', xKey: 'date', yKey: ENVELOPE_KEY,
        yName: t(`${i18nPrefix}.projected`),
        stroke: theme.warningColor,
        fill: theme.warningColor, fillOpacity: 0.15,
        tooltip: { enabled: false },
      });
    }

    const hasRecommendationPabData = chartData.some(row => row[RECOMMENDATION_PAB_KEY] != null);
    if (hasRecommendationPabData) {
      seriesConfig.push({
        type: 'line', xKey: 'date', yKey: RECOMMENDATION_PAB_KEY,
        yName: t('inventory.timeline.recommendationPab'),
        stroke: theme.successColor,
        lineDash: [6, 3],
      });
    }

    const hasTargetInventory = chartData.some(row => row[TARGET_INVENTORY_KEY] != null);
    if (hasTargetInventory) {
      const targetLabel = t([`${i18nPrefix}.targetInventory`, 'charts.common.targetInventory']);
      seriesConfig.push({
        type: 'line', xKey: 'date', yKey: TARGET_INVENTORY_KEY,
        yName: targetLabel,
        stroke: theme.warningColor,
        lineDash: [6, 3],
      });
    }

    const unstackedKeys = [ENVELOPE_KEY, RECOMMENDATION_PAB_KEY, TARGET_INVENTORY_KEY];
    let yDataMax = 0;
    for (const row of chartData) {
      let barSum = 0;
      let fcSum = 0;
      for (const key of allGroupKeys) {
        const bv = row[key];
        if (typeof bv === 'number' && Number.isFinite(bv)) barSum += bv;
        if (hasForecastGroups) {
          const fv = row[`fc_${key}`];
          if (typeof fv === 'number' && Number.isFinite(fv)) fcSum += fv;
        }
      }
      if (barSum > yDataMax) yDataMax = barSum;
      if (fcSum > yDataMax) yDataMax = fcSum;
      for (const k of unstackedKeys) {
        const v = row[k];
        if (typeof v === 'number' && Number.isFinite(v) && v > yDataMax) yDataMax = v;
      }
    }
    const yMaxPadded = headroomMax(yDataMax);

    const totalWord = t(`${i18nPrefix}.total`);
    const totalLabels = !chartData.some(row => row[TOTAL_KEY] != null)
      ? null
      : hasForecastGroups
        ? {
          [BAR_TOTAL_KEY]: `${totalWord} — ${salesLabel}`,
          [FORECAST_TOTAL_KEY]: `${totalWord} — ${forecastLabel}`,
        }
        : { [BAR_TOTAL_KEY]: totalWord };

    return { chartData, seriesConfig, yMaxPadded, totalLabels };
  }, [historyData, projectionData, allPeriods, currentPeriod, hiddenGroups, t, i18nPrefix, theme, fetchedData?.target_inventory]);

  const yLabelFormatter = useMemo(() => (params) => {
    if (metric === 'value') return formatCompact(params.value);
    return formatNumber(params.value, 0);
  }, [metric, formatNumber, formatCompact]);

  const chartOptions = useMemo(() => {
    if (chartData.length === 0 || seriesConfig.length === 0) return null;

    const hasProjection = projectionData != null;
    const bucket = periodBucket(chartData[0].period);

    const formatTooltipValue = (v) => (metric === 'value'
      ? formatCurrency(v, fetchedData.currency)
      : formatNumber(v, 0));
    const tooltipRenderer = ({ datum, yKey, yName }) => (
      { data: [{ label: yName, value: formatTooltipValue(datum[yKey]) }] }
    );

    const series = seriesConfig.map(s => (s.tooltip ? s : { ...s, tooltip: { renderer: tooltipRenderer } }));
    if (totalLabels) {
      series.push({
        type: 'line', xKey: 'date', yKey: TOTAL_KEY,
        showInLegend: false,
        strokeWidth: 0,
        tooltip: {
          renderer: ({ datum }) => tooltipTotalHtml(
            Object.entries(totalLabels)
              .filter(([key]) => datum[key] != null)
              .map(([key, label]) => ({ label, value: formatTooltipValue(datum[key]) })),
          ),
        },
      });
    }

    const crossLines = [];

    if (Array.isArray(fetchedData?.promotions)) {
      for (const promo of fetchedData.promotions) {
        crossLines.push({
          type: 'range',
          range: [bucketOpeningOf(promo.start_date, bucket), bucketOpeningOf(promo.end_date, bucket)],
          fill: theme.warningColor,
          fillOpacity: 0.08,
          stroke: theme.warningColor,
          strokeWidth: 1,
          strokeOpacity: 0.4,
          label: { text: promo.name, position: 'inside-top' },
        });
      }
    }

    if (Array.isArray(fetchedData?.stockout_periods)) {
      for (const period of fetchedData.stockout_periods) {
        crossLines.push({
          type: 'line',
          value: periodToDate(period),
          stroke: theme.criticalColor,
          strokeWidth: 1.5,
          lineDash: [2, 2],
          label: {
            text: t([`${i18nPrefix}.stockout`, 'charts.common.stockout']),
            position: 'bottom',
          },
        });
      }
    }

    if (hasProjection && currentPeriod) {
      crossLines.push({
        type: 'line',
        value: periodToDate(currentPeriod),
        stroke: theme.textSecondary,
        strokeWidth: 2,
        lineDash: [4, 4],
        label: {
          text: t(`${i18nPrefix}.today`),
          position: 'top',
        },
      });
    }

    const opts = {
      data: chartData,
      listeners: {
        seriesVisibilityChange: ({ itemId, visible }) => {
          if (NON_GROUP_KEYS.has(itemId)) return;
          setHiddenGroups(prev => {
            const next = new Set(prev);
            visible ? next.delete(itemId) : next.add(itemId);
            return next;
          });
        },
        ...(showNavigator && setNavigatorZoom ? {
          zoom: (e) => {
            if (e.source !== 'user-interaction') return;
            setNavigatorZoom({ start: e.ratioX.start, end: e.ratioX.end });
          },
        } : {}),
      },
      series,
      axes: {
        x: {
          type: 'ordinal-time',
          crossLines,
          label: { formatter: ({ value }) => formatPeriodLabel(value, bucket) },
          paddingOuter: 0,
        },
        y: {
          type: 'number',
          label: { formatter: yLabelFormatter },
          preferredMin: 0,
          ...(yMaxPadded != null ? { max: yMaxPadded } : {}),
          nice: true,
        },
      },
      tooltip: { mode: 'shared', range: 20 },
      legend: { enabled: true },
    };

    if (showNavigator) {
      const handle = { fill: theme.surfaceColor, stroke: theme.gridColor };
      opts.navigator = {
        enabled: true,
        miniChart: { enabled: false },
        mask: { fill: theme.fillColor, fillOpacity: 1, stroke: theme.gridColor },
        minHandle: handle,
        maxHandle: handle,
      };
      opts.zoom = {
        enabled: true,
        enableScrolling: false,
        enableAxisDragging: false,
        enableAxisScrolling: false,
        axes: 'x',
        minVisibleItems: 3,
        autoScaling: { enabled: true },
        buttons: { enabled: false },
      };
      if (savedNavigatorZoom) {
        opts.initialState = { zoom: { ratioX: { start: savedNavigatorZoom.start, end: savedNavigatorZoom.end } } };
      } else if (hasProjection && currentPeriod) {
        const todayIdx = allPeriods.indexOf(currentPeriod);
        if (todayIdx >= 0) {
          const before = navigatorWindow?.before ?? 2;
          const zoomStart = Math.max(0, (todayIdx - before) / Math.max(allPeriods.length - 1, 1));
          let zoomEnd = 1;
          if (navigatorWindow?.after != null) {
            const endIdx = Math.min(allPeriods.length - 1, todayIdx + navigatorWindow.after);
            zoomEnd = endIdx / Math.max(allPeriods.length - 1, 1);
          }
          opts.initialState = { zoom: { ratioX: { start: zoomStart, end: zoomEnd } } };
        }
      }
    }

    return opts;
  }, [chartData, seriesConfig, yMaxPadded, totalLabels, yLabelFormatter, theme, currentPeriod, allPeriods, projectionData, showNavigator, navigatorWindow, savedNavigatorZoom, setNavigatorZoom, t, i18nPrefix, fetchedData?.promotions, fetchedData?.currency, fetchedData?.stockout_periods, formatCurrency, formatNumber, formatPeriodLabel, metric]);

  const metricOptions = [
    { value: 'value', label: t(`${i18nPrefix}.value`) },
    { value: 'qty', label: t(`${i18nPrefix}.qty`) },
  ];

  const granularityOptions = [
    { value: 'month', label: t(`${i18nPrefix}.month`) },
    { value: 'week', label: t(`${i18nPrefix}.week`) },
    { value: 'day', label: t(`${i18nPrefix}.day`) },
  ];

  const isValue = metric === 'value';
  const uncostedItems = isValue && fetchedData?.partial_cost ? fetchedData.uncosted_items : 0;
  if (!Number.isInteger(uncostedItems)) {
    throw new Error('inventory timeline: partial_cost is set without uncosted_items');
  }
  const unpricedItems = isValue && fetchedData?.partial_sell_price ? fetchedData.unpriced_items : 0;
  if (!Number.isInteger(unpricedItems)) {
    throw new Error('demand timeline: partial_sell_price is set without unpriced_items');
  }
  if (isValue && chartOptions && !fetchedData.currency) {
    throw new Error('timeline: value payload carries no currency');
  }
  if (importingReason !== undefined && !EMPTY_REASON_KEYS[importingReason]) {
    throw new Error(`BarEnvelopeChart: importingReason has no sentence (got ${importingReason})`);
  }
  const stateReason = importingReason ?? emptyReason;
  const reasonKey = EMPTY_REASON_KEYS[stateReason];
  const showState = Boolean(importingReason) || !chartOptions;
  const noCostEmpty = stateReason === 'no_cost_data';
  const hasPriceCaveats = !showState
    && (uncostedItems > 0 || unpricedItems > 0);

  const hasControls = controls.metric || controls.granularity;

  return (
    <div className="bar-envelope-wrapper">
      {hasControls && (
        <div className="bar-envelope-controls">
          {controls.metric && (
            <SegmentControl options={metricOptions} value={metric} onChange={setMetric} />
          )}
          {controls.granularity && (
            <SegmentControl options={granularityOptions} value={granularity} onChange={setGranularity} />
          )}
        </div>
      )}

      {hasPriceCaveats && (
        <div className="bar-envelope-caveats">
          <PartialPriceChip gap="cost" count={uncostedItems} />
          <PartialPriceChip gap="sellPrice" count={unpricedItems} />
        </div>
      )}

      <div className="bar-envelope-chart">
        {loading ? (
          <div className="chart-state">
            <CircularProgress size={36} />
          </div>
        ) : error ? (
          <div className="chart-state">
            <Typography color="error">{error}</Typography>
          </div>
        ) : showState ? (
          <div className={`chart-state${noCostEmpty ? ' chart-state--action' : ''}`}>
            <Typography color="textSecondary">
              {reasonKey ? t(reasonKey) : noDataKey ? t(noDataKey) : t(`${i18nPrefix}.noData`)}
            </Typography>
            {noCostEmpty && (
              <Button component={TabLink} to="/data-health" size="small" variant="outlined">
                {t('charts.common.openDataHealth')}
              </Button>
            )}
          </div>
        ) : (
          <Chart options={chartOptions} className="chart-fill" />
        )}
      </div>
    </div>
  );
}

export default BarEnvelopeChart;
